import { useEffect, useState } from 'react';

/**
 * usePrefersReducedMotion — live subscription to the OS "reduce motion" setting.
 *
 * Used to switch off chart/number/entrance animations (charts pass `!reduced`
 * to recharts `isAnimationActive`; <FadeIn>/<Stagger> render statically).
 * SSR-safe: defaults to `false` (motion allowed) when matchMedia is unavailable.
 */
const QUERY = '(prefers-reduced-motion: reduce)';

export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(QUERY).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(QUERY);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    // Modern browsers: addEventListener. Safari < 14: addListener.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  return reduced;
}
